import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { ONE_WEEK_IN_HOURS } from '../../../../basics/constants/common.constants';
import {
  ReminderItemState,
  ReminderScheduleState,
  MarketDefaultStatus,
} from './ReminderScheduleGroup.types';
import { warningToast } from 'basics/utils/toast';
import { EmailReminderType, Market, Presence, ReminderChannel, ReminderJourneyOverride, ReminderJourneys } from 'generated/event.types';
import { isCxSurveyReminder } from 'modules/events/basics/utils/cxSurveyReminderSync.utils';
import { useClearEventReminderOverridesMutation } from 'modules/events/graphql/mutations/clearEventReminderOverrides';
import { useSetEventEmailConfigMutation } from 'modules/events/graphql/mutations/setEventEmailConfig';
import { useSetEventReminderOverridesMutation } from 'modules/events/graphql/mutations/setEventReminderOverrides';
import useGetReminderJourneys from 'modules/events/graphql/queries/getReminderJourneys';
import eventState from 'modules/events/states/events.states';

const REGISTRATION_CONFIRMATION_ID = 'registration-confirmation';
const ONE_HOUR_REMINDER_TIME = 1;
const WHATSAPP_SUPPORTED_MARKETS: Market[] = [Market.fr];

const isSmsReminder = (journey: ReminderJourneys): boolean => journey.channel === ReminderChannel.sms
  || journey.name.toUpperCase().includes('SMS');

const isWhatsappReminder = (journey: ReminderJourneys): boolean => journey.channel === ReminderChannel.whatsapp
  || journey.name.toUpperCase().includes('WHATSAPP');

const getSendTimeText = (
  journey: ReminderJourneys,
  t: (key: string, params?: Record<string, string | number>) => string,
): string => {
  if (journey.format === EmailReminderType.after_end) {
    return t('reminder_schedule_send_time_post_event');
  }

  if (journey.format === EmailReminderType.before_start) {
    const hours = journey.timeInHour || 0;
    if (hours === ONE_WEEK_IN_HOURS) {
      // 1 week = 168 hours
      return t('reminder_schedule_send_time_1_week');
    }
    return t('reminder_schedule_send_time_hours_from_event', { hours });
  }

  return '';
};

const getSendDateDescription = (
  format: string,
  t: (key: string) => string,
  enabled: boolean,
): string => {
  if (!enabled) {
    return t('reminder_schedule_not_included');
  }
  if (format === 'transactional') {
    return t('reminder_schedule_fires_on_signup');
  }
  if (format === EmailReminderType.after_end) {
    return t('reminder_schedule_sends_post_event');
  }
  // TODO: Replace with actual calculated date from backend when available
  return t('reminder_schedule_sends_date_placeholder');
};

const isEnabledByDefaultForMarket = (journey: ReminderJourneys, market: Market | undefined): boolean => {
  if (!market || !journey.marketDefaults) {
    return false;
  }
  return journey.marketDefaults.includes(market);
};

const createRegistrationConfirmationReminder = (
  t: (key: string) => string,
  enabled = true,
): ReminderItemState => ({
  journeyId: REGISTRATION_CONFIRMATION_ID,
  name: t('reminder_schedule_registration_confirmation'),
  sendDateDescription: enabled
    ? t('reminder_schedule_fires_on_signup')
    : t('reminder_schedule_not_included'),
  sendTimeText: t('reminder_schedule_send_time_transactional'),
  marketDefault: MarketDefaultStatus.onByDefault,
  enabled,
  isRequired: false,
  isEnabledByDefault: true,
  format: 'transactional',
});

const useReminderSchedule = () => {
  const { t } = useTranslation();
  const snap = useSnapshot(eventState);

  const eventId = snap.createEvent._id;
  const eventMarket = snap.createEvent.market;
  const eventPresence = snap.createEvent.presence;
  const eventOverrides = snap.createEvent.reminderConfig?.overrides;
  const confirmationEmailEnabled = snap.createEvent.data?.confirmationEmailEnabled !== false;
  const cxSurveyReminderEnabledFromValtio = snap.createEvent.cxSurveyReminderEnabled;

  const lastEmittedCxSurveyValue = useRef<boolean | undefined>(undefined);
  const cxSurveySyncSettled = useRef(false);

  // Online and hybrid events require at least one before_start reminder to be enabled
  const requiresAtLeastOneReminder = eventPresence === Presence.online || eventPresence === Presence.hybrid;
  const { data: journeys, loading: journeysLoading, refetch: refetchJourneys } = useGetReminderJourneys();
  const [setEventReminderOverrides, { loading: saveLoading }] = useSetEventReminderOverridesMutation();
  const [clearEventReminderOverrides, { loading: resetLoading }] = useClearEventReminderOverridesMutation();
  const [setEventEmailConfig] = useSetEventEmailConfigMutation();

  const marketLabel = useMemo(() => {
    if (!eventMarket) {
      return '';
    }
    return t(`market_label_${eventMarket}`);
  }, [eventMarket, t]);

  const transformJourneysToReminders = useCallback((
    apiJourneys: ReminderJourneys[],
    market?: Market,
    overrides?: ReminderJourneyOverride[],
    isConfirmationEnabled = true,
    presence?: Presence,
  ): ReminderItemState[] => {
    const overridesMap = new Map<string, boolean>();
    if (overrides) {
      overrides.forEach((override) => {
        overridesMap.set(override.journeyId, override.enabled);
      });
    }

    const journeyReminders = apiJourneys
      .filter((journey) => journey.active)
      .map((journey) => {
        const isSms = isSmsReminder(journey);
        const isWhatsapp = isWhatsappReminder(journey);

        const isWhatsappAvailable = isWhatsapp && market
          ? WHATSAPP_SUPPORTED_MARKETS.includes(market)
          : false;
        const isSmsAvailable = isSms ? isEnabledByDefaultForMarket(journey, market) : false;
        const isDisabledForMarket = (isSms && !isSmsAvailable) || (isWhatsapp && !isWhatsappAvailable);

        const isEnabledByDefault = isSms
          ? isSmsAvailable
          : (isWhatsapp ? false : isEnabledByDefaultForMarket(journey, market));

        const marketDefault = isWhatsapp
          ? MarketDefaultStatus.offByDefault
          : (isEnabledByDefault ? MarketDefaultStatus.onByDefault : MarketDefaultStatus.offByDefault);

        const isOneWeekReminder = journey.format === EmailReminderType.before_start
          && journey.timeInHour === ONE_WEEK_IN_HOURS;
        // TODO: Temporary hardcode - skip 1 week reminder for Online events except DE and GB
        const isDisabledForOnline = isOneWeekReminder
        && presence === Presence.online && market !== Market.de && market !== Market.gb && market !== Market.com;

        const overrideEnabled = overridesMap.get(journey._id);
        let enabled: boolean;
        if (isDisabledForOnline) {
          enabled = false;
        } else if (overrideEnabled !== undefined) {
          enabled = overrideEnabled;
        } else if (isDisabledForMarket) {
          enabled = false;
        } else {
          enabled = isEnabledByDefault;
        }

        return {
          journeyId: journey._id,
          name: journey.name,
          sendDateDescription: getSendDateDescription(journey.format, t, enabled),
          sendTimeText: getSendTimeText(journey, t),
          marketDefault,
          enabled,
          isRequired: false,
          isEnabledByDefault,
          format: journey.format,
          timeInHour: journey.timeInHour,
          isSms,
          isWhatsapp,
          isDisabledForMarket: isDisabledForMarket || isDisabledForOnline,
          supportedMarkets: isWhatsapp
            ? WHATSAPP_SUPPORTED_MARKETS.map((supportedMarket) => t(`market_label_${supportedMarket}`))
            : (isSms
              ? (journey.marketDefaults || []).map((defaultMarket) => t(`market_label_${defaultMarket}`))
              : undefined),
        };
      });

    const registrationConfirmation = createRegistrationConfirmationReminder(t, isConfirmationEnabled);

    return [registrationConfirmation, ...journeyReminders];
  }, [t]);

  const [state, setState] = useState<ReminderScheduleState>({
    allRemindersEnabled: true,
    reminders: [],
  });

  useEffect(() => {
    if (journeys) {
      const reminders = transformJourneysToReminders(
        journeys, eventMarket, eventOverrides as ReminderJourneyOverride[], confirmationEmailEnabled, eventPresence,
      );

      setState((prevState) => {
        const prevConfirmation = prevState.reminders.find(
          (reminder) => reminder.journeyId === REGISTRATION_CONFIRMATION_ID,
        );
        const updatedReminders = prevConfirmation
          ? reminders.map((reminder) => (reminder.journeyId === REGISTRATION_CONFIRMATION_ID ? prevConfirmation : reminder))
          : reminders;

        const allNonRequiredEnabled = updatedReminders
          .filter((reminder) => !reminder.isRequired)
          .every((reminder) => reminder.enabled);

        return {
          allRemindersEnabled: allNonRequiredEnabled,
          reminders: updatedReminders,
        };
      });
    }
  }, [journeys, eventMarket, eventOverrides, confirmationEmailEnabled, eventPresence, transformJourneysToReminders]);

  useEffect(() => {
    if (!eventId) return;

    const cxSurveyReminder = state.reminders.find(
      (reminder) => isCxSurveyReminder(reminder.name),
    );

    if (cxSurveyReminder === undefined) return;
    if (cxSurveyReminder.enabled === lastEmittedCxSurveyValue.current) return;

    lastEmittedCxSurveyValue.current = cxSurveyReminder.enabled;
    eventState.createEvent.cxSurveyReminderEnabled = cxSurveyReminder.enabled;
  }, [state.reminders, eventId]);

  const handleAllRemindersToggle = useCallback((checked: boolean) => {
    const shouldKeep1hReminder = !checked && requiresAtLeastOneReminder;

    if (shouldKeep1hReminder) {
      warningToast(t('reminder_schedule_at_least_one_required'));
    }

    setState((prevState) => {
      const hasSmsAvailable = prevState.reminders.some((reminder) => reminder.isSms && !reminder.isDisabledForMarket);

      return {
        ...prevState,
        allRemindersEnabled: checked,
        reminders: prevState.reminders.map((reminder) => {
          if (reminder.isRequired || reminder.isDisabledForMarket) {
            return reminder;
          }
          const is1hBeforeStartEmailReminder = reminder.format === EmailReminderType.before_start
            && reminder.timeInHour === ONE_HOUR_REMINDER_TIME
            && !reminder.isSms && !reminder.isWhatsapp;
          if (shouldKeep1hReminder && is1hBeforeStartEmailReminder) {
            return {
              ...reminder,
              enabled: true,
              sendDateDescription: getSendDateDescription(reminder.format, t, true),
            };
          }
          if (checked && reminder.isWhatsapp && hasSmsAvailable) {
            return {
              ...reminder,
              enabled: false,
              sendDateDescription: getSendDateDescription(reminder.format, t, false),
            };
          }
          return {
            ...reminder,
            enabled: checked,
            sendDateDescription: getSendDateDescription(reminder.format, t, checked),
          };
        }),
      };
    });
  }, [t, requiresAtLeastOneReminder]);

  const handleReminderToggle = useCallback((journeyId: string, checked: boolean) => {
    if (!checked && requiresAtLeastOneReminder) {
      const enabledBeforeStartEmailReminders = state.reminders.filter(
        (reminder) => reminder.enabled
          && reminder.format === EmailReminderType.before_start
          && !reminder.isSms && !reminder.isWhatsapp
          && !reminder.isRequired,
      );
      const targetReminder = state.reminders.find((r) => r.journeyId === journeyId);
      const isTargetBeforeStartEmailReminder = targetReminder
        && targetReminder.format === EmailReminderType.before_start
        && !targetReminder.isSms && !targetReminder.isWhatsapp;
      const isLastBeforeStartEmailReminder = enabledBeforeStartEmailReminders.length === 1
        && isTargetBeforeStartEmailReminder;

      if (isLastBeforeStartEmailReminder) {
        warningToast(t('reminder_schedule_at_least_one_required'));
        return;
      }
    }

    setState((prevState) => {
      const smsOrWhatsAppTargetReminder = prevState.reminders.find((reminder) => reminder.journeyId === journeyId);
      const isEnablingSms = checked && smsOrWhatsAppTargetReminder?.isSms;
      const isEnablingWhatsapp = checked && smsOrWhatsAppTargetReminder?.isWhatsapp;

      const updatedReminders = prevState.reminders.map((reminder) => {
        if (reminder.journeyId === journeyId) {
          return {
            ...reminder,
            enabled: checked,
            sendDateDescription: getSendDateDescription(reminder.format, t, checked),
          };
        }
        if (isEnablingSms && reminder.isWhatsapp && !reminder.isDisabledForMarket) {
          return {
            ...reminder,
            enabled: false,
            sendDateDescription: getSendDateDescription(reminder.format, t, false),
          };
        }
        if (isEnablingWhatsapp && reminder.isSms && !reminder.isDisabledForMarket) {
          return {
            ...reminder,
            enabled: false,
            sendDateDescription: getSendDateDescription(reminder.format, t, false),
          };
        }
        return reminder;
      });

      const allNonRequiredEnabled = updatedReminders
        .filter((r) => !r.isRequired)
        .every((r) => r.enabled);

      return {
        ...prevState,
        reminders: updatedReminders,
        allRemindersEnabled: allNonRequiredEnabled,
      };
    });
  }, [t, requiresAtLeastOneReminder, state.reminders]);

  useEffect(() => {
    if (!eventId) return;
    if (cxSurveyReminderEnabledFromValtio === undefined) return;

    if (cxSurveyReminderEnabledFromValtio === lastEmittedCxSurveyValue.current) {
      cxSurveySyncSettled.current = true;
      return;
    }

    if (!cxSurveySyncSettled.current) return;

    const cxSurveyReminder = state.reminders.find(
      (reminder) => isCxSurveyReminder(reminder.name),
    );

    if (!cxSurveyReminder) return;
    if (cxSurveyReminder.enabled === cxSurveyReminderEnabledFromValtio) return;

    lastEmittedCxSurveyValue.current = cxSurveyReminderEnabledFromValtio;

    handleReminderToggle(cxSurveyReminder.journeyId, cxSurveyReminderEnabledFromValtio);

    const updatedReminders = state.reminders.map((reminder) => (reminder.journeyId === cxSurveyReminder.journeyId
      ? { ...reminder, enabled: cxSurveyReminderEnabledFromValtio }
      : reminder));

    const overrides = updatedReminders
      .filter((reminder) => {
        if (reminder.isDisabledForMarket || reminder.journeyId === REGISTRATION_CONFIRMATION_ID) return false;
        return reminder.enabled !== reminder.isEnabledByDefault;
      })
      .map((reminder) => ({
        journeyId: reminder.journeyId,
        enabled: reminder.enabled,
      }));

    setEventReminderOverrides({ eventId, overrides }).then((result) => {
      if (result) {
        const currentOverrides = eventState.createEvent.reminderConfig?.overrides;
        const isSame = currentOverrides
          && currentOverrides.length === overrides.length
          && currentOverrides.every((cur, i) => cur.journeyId === overrides[i].journeyId && cur.enabled === overrides[i].enabled);
        if (!isSame) {
          eventState.createEvent.reminderConfig = { overrides };
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cxSurveyReminderEnabledFromValtio, eventId]);

  const handleResetToDefaults = useCallback(async () => {
    if (!eventId) {
      return;
    }

    const result = await clearEventReminderOverrides(eventId);

    if (result && journeys) {
      const reminders = transformJourneysToReminders(journeys, eventMarket, [], true, eventPresence);
      const allNonRequiredEnabled = reminders
        .filter((r) => !r.isRequired)
        .every((r) => r.enabled);

      setState({
        allRemindersEnabled: allNonRequiredEnabled,
        reminders,
      });

      eventState.createEvent.reminderConfig = { overrides: [] };
    }
  }, [eventId, eventMarket, eventPresence, journeys, clearEventReminderOverrides, transformJourneysToReminders]);

  const handleSaveConfiguration = useCallback(async () => {
    if (!eventId) {
      return;
    }

    const confirmationReminder = state.reminders.find(
      (reminder) => reminder.journeyId === REGISTRATION_CONFIRMATION_ID,
    );

    const overrides: ReminderJourneyOverride[] = state.reminders
      .filter((reminder) => {
        if (reminder.journeyId === REGISTRATION_CONFIRMATION_ID) {
          return false;
        }
        if (reminder.isDisabledForMarket) {
          return false;
        }
        return reminder.enabled !== reminder.isEnabledByDefault;
      })
      .map((reminder) => ({
        journeyId: reminder.journeyId,
        enabled: reminder.enabled,
      }));

    const result = await setEventReminderOverrides({
      eventId,
      overrides,
    });

    if (confirmationReminder) {
      await setEventEmailConfig({
        eventId,
        confirmationEmailEnabled: confirmationReminder.enabled,
      });
    }

    if (result) {
      eventState.createEvent.reminderConfig = { overrides };
    }
  }, [eventId, state.reminders, setEventReminderOverrides, setEventEmailConfig]);

  const getMarketDefaultLabel = useCallback((status: MarketDefaultStatus): string => {
    switch (status) {
      case MarketDefaultStatus.required:
        return t('reminder_schedule_market_default_required');
      case MarketDefaultStatus.onByDefault:
        return t('reminder_schedule_market_default_on');
      case MarketDefaultStatus.offByDefault:
        return t('reminder_schedule_market_default_off');
      default:
        return '';
    }
  }, [t]);

  return {
    t,
    eventMarket,
    marketLabel,
    state,
    loading: journeysLoading,
    saveLoading,
    resetLoading,
    handleAllRemindersToggle,
    handleReminderToggle,
    handleResetToDefaults,
    handleSaveConfiguration,
    getMarketDefaultLabel,
    refetchJourneys,
  };
};

export default useReminderSchedule;
